import React, { createContext, useContext, ReactNode, useState } from "react";
import { uploadData, TransferProgressEvent } from "aws-amplify/storage";

export const CLINIC_OVERRIDE_KEY = "superadmin-clinic-id";

class ApiService {
  private baseUrl: string;
  private setLoading: (loading: boolean) => void;
  private accessToken: string;
  private publicToken: string;
  private clinicOverride: string | null;

  private static instance: ApiService;

  constructor(
    baseUrl: string,
    publicToken: string,
    setLoading: (loading: boolean) => void,
    accessToken: string = ""
  ) {
    this.baseUrl = baseUrl;
    this.setLoading = setLoading;
    this.accessToken = accessToken;
    this.publicToken = publicToken;
    this.clinicOverride = localStorage.getItem(CLINIC_OVERRIDE_KEY);
  }

  public static getInstance(
    baseUrl: string = "",
    publicToken: string = "",
    setLoading: (loading: boolean) => void,
    accessToken: string = ""
  ): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService(
        baseUrl,
        publicToken,
        setLoading,
        accessToken
      );
    }
    return ApiService.instance;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit,
    ignoreResponse: boolean = false
  ): Promise<T> {
    try {
      const commonHeaders: Record<string, string> = {
        "x-api-key": this.publicToken,
        "Content-Type": "application/json",
      };

      if (this.accessToken) {
        commonHeaders["Authorization"] = `Bearer ${this.accessToken}`;
      }

      // SuperAdmins send the clinic they are currently acting on. The backend
      // ignores this header unless the verified JWT role is SuperAdmin.
      if (this.clinicOverride) {
        commonHeaders["X-Clinic-Id"] = this.clinicOverride;
      }

      options.headers = { ...options.headers, ...commonHeaders };

      console.log("API request:", endpoint, options);

      const response = await fetch(`${this.baseUrl}${endpoint}`, options);

      if (!response.ok) {
        if (response.status === 403) {
          window.location.href = "/login";
        }

        console.error("API request error:", response);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      if (ignoreResponse) {
        return {} as T;
      }
      return await response.json();
    } catch (error: any) {
      console.error("API request error:", error);
      throw error;
    }
  }

  public setAccessToken(token: string) {
    console.info("API: Auth: setting access token");
    this.accessToken = token;
  }

  public setClinicOverride(clinicId: string | null) {
    this.clinicOverride = clinicId;
    if (clinicId) {
      localStorage.setItem(CLINIC_OVERRIDE_KEY, clinicId);
    } else {
      localStorage.removeItem(CLINIC_OVERRIDE_KEY);
    }
  }

  public getClinicOverride(): string | null {
    return this.clinicOverride;
  }

  public async head<T>(endpoint: string): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: "HEAD",
      },
      true
    );
  }

  public async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: "GET",
    });
  }

  public async delete<T, U>(endpoint: string, data: U): Promise<T> {
    return this.request<T>(endpoint, {
      method: "DELETE",
      body: JSON.stringify(data),
    });
  }

  public async post<T, U>(endpoint: string, data: U): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  public async put<T, U>(endpoint: string, data: U): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  public async upload(
    file: File,
    path: string,
    filename: string,
    onProgress: (transferred: number, total: number) => void = () => {}
  ): Promise<string> {
    try {
      console.log("Uploading file", file, path, filename);
      const response = await uploadData({
        path: `${path}/${filename}`,
        data: file,
        options: {
          onProgress(event: TransferProgressEvent) {
            console.log(
              `Uploaded: ${event.transferredBytes}/${event.totalBytes}`
            );
            if (event.totalBytes) {
              onProgress(event.transferredBytes, event.totalBytes);
            } else {
              console.warn("upload progress: totalBytes is 0");
            }
          },
        },
      });
      return (await response.result).path;
    } catch (error) {
      console.error("Error uploading file", error);
      throw error;
    }
  }
}

interface ApiContextType {
  apiService: ApiService;
  isLoading: boolean;
  isSubmitting: boolean;
  setSubmitting: (submitting: boolean) => void;
  setLoading: (loading: boolean) => void;
}

const ApiContext = createContext<ApiContextType | undefined>(undefined);

interface ApiProviderProps {
  baseUrl: string;
  children: ReactNode;
  publicToken: string;
}

export const ApiProvider: React.FC<ApiProviderProps> = ({
  baseUrl,
  children,
  publicToken,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const setLoading = (loading: boolean) => {
    setIsLoading(loading);
  };

  const setSubmitting = (submitting: boolean) => {
    setIsSubmitting(submitting);
  };

  const apiService = ApiService.getInstance(baseUrl, publicToken, setLoading);

  return (
    <ApiContext.Provider
      value={{ apiService, isSubmitting, setSubmitting, isLoading, setLoading }}
    >
      {children}
    </ApiContext.Provider>
  );
};

export const useApi = (): ApiContextType => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("useApi must be used within an ApiProvider");
  }
  return context;
};
